import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi, permissionsApi, entitlementsApi } from "../lib/api";
import { clearThemeCache } from "../components/CommandThemeScope";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = checking | object = authenticated | false = unauthenticated
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isAdminPerm, setIsAdminPerm] = useState(false);
  const [modules, setModules] = useState([]);
  const [plan, setPlan] = useState(null);
  const [entLoaded, setEntLoaded] = useState(false);

  const loadPermissions = useCallback(async () => {
    try {
      const p = await permissionsApi.me();
      setPermissions(p.permissions || []);
      setIsAdminPerm(!!p.isAdmin);
    } catch (_) {
      setPermissions([]);
      setIsAdminPerm(false);
    }
  }, []);

  const loadEntitlements = useCallback(async () => {
    try {
      const e = await entitlementsApi.me();
      setModules(e.modules || []);
      setPlan(e.plan || null);
    } catch (_) {
      setModules([]);
      setPlan(null);
    } finally {
      setEntLoaded(true);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      loadPermissions();
      loadEntitlements();
      return u;
    } catch (_) {
      setUser(false);
      setPermissions([]);
      return false;
    }
  }, [loadPermissions, loadEntitlements]);

  useEffect(() => {
    (async () => {
      await Promise.resolve();
      // CRITICAL (playbook): If returning from Emergent OAuth, skip the /me check.
      // AuthCallback will exchange the session_id and establish the JWT cookies first.
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        setUser(false); // stay un-rendered as authed; AuthCallback will navigate
        return;
      }
      // Public, no-auth pages (e.g. the customer invoice pay page) shouldn't fire
      // an authenticated /me probe — avoids needless 401s for anonymous visitors.
      if (typeof window !== "undefined" && (window.location.pathname.startsWith("/pay/") || window.location.pathname.startsWith("/accept/") || window.location.pathname.startsWith("/f/"))) {
        setUser(false);
        return;
      }
      refreshMe();
    })();
  }, [refreshMe]);

  // Listen for 401 from api interceptor
  useEffect(() => {
    const handler = () => setUser(false);
    window.addEventListener("auth-expired", handler);
    return () => window.removeEventListener("auth-expired", handler);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    setUser(res.user);
    // Ask the user whether they want to clock in, but only for tenant users —
    // super_admins go straight to the Platform Console and never see it.
    if (res.user && res.user.role !== "super_admin") {
      try { sessionStorage.setItem("clockin_prompt_pending", "1"); } catch (_) { /* private mode */ }
    }
    loadPermissions();
    loadEntitlements();
    return res.user;
  }, [loadPermissions, loadEntitlements]);

  const register = useCallback(async (data) => {
    const res = await authApi.register(data);
    setUser(res.user);
    if (res.user && res.user.role !== "super_admin") {
      try { sessionStorage.setItem("clockin_prompt_pending", "1"); } catch (_) { /* private mode */ }
    }
    loadPermissions();
    loadEntitlements();
    return res;
  }, [loadPermissions, loadEntitlements]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (_) {
      /* ignore */
    }
    setUser(false);
    setPermissions([]);
    setIsAdminPerm(false);
    clearThemeCache();
  }, []);

  // can(key) — true if the user is granted a permission. Admins always pass.
  const can = useCallback(
    (key) => {
      if (!key) return true;
      if (isAdminPerm || (user && (user.role === "admin" || user.role === "super_admin"))) return true;
      return permissions.includes(key);
    },
    [permissions, isAdminPerm, user]
  );

  // hasModule(key) — true if the company's plan includes the gateable module.
  // Returns true while entitlements are still loading to avoid a lock flash.
  const hasModule = useCallback(
    (key) => {
      if (!key) return true;
      if (user && user.role === "super_admin") return true;
      if (!entLoaded) return true;
      return modules.includes(key);
    },
    [modules, entLoaded, user]
  );

  return (
    <AuthContext.Provider value={{ user, permissions, can, modules, plan, hasModule, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Map FastAPI error payloads to a plain string so React doesn't crash on objects.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}
